import type { SVGProps } from "react";

// A small hand-picked icon set (Feather-style, 1.5px stroke) covering exactly
// what the nav and pages need, so the app ships no icon-font/library
// dependency for a couple dozen glyphs.
const PATHS: Record<string, string> = {
  home: "M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5Z",
  users:
    "M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75",
  building: "M4 21V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v17 M14 21V9a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v12 M8 7h.01 M8 11h.01 M8 15h.01",
  "calendar-check": "M8 2v4 M16 2v4 M3 10h18 M3 6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6Z M9 16l2 2 4-4",
  calendar: "M8 2v4 M16 2v4 M3 10h18 M3 6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6Z",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z M12 7v5l3 3",
  "alert-triangle": "M10.3 3.86 1.82 18a1 1 0 0 0 .86 1.5h18.64a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.73 0Z M12 9v4 M12 17h.01",
  fingerprint:
    "M2 12a10 10 0 0 1 18-6 M22 12a10 10 0 0 1-3 7.1 M6 12a6 6 0 0 1 11-3.5 M6 12a6 6 0 0 0 9 5.2 M9 12a3 3 0 0 1 5.5-1.7 M9 12a3 3 0 0 0 3.5 2.9",
  plug: "M12 22v-5 M9 8V2 M15 8V2 M18 8H6a1 1 0 0 0-1 1v3a7 7 0 0 0 14 0V9a1 1 0 0 0-1-1Z",
  wallet: "M21 12V7H5a2 2 0 0 1 0-4h14v4 M3 5v14a2 2 0 0 0 2 2h16v-5 M18 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z",
  "file-text": "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z M14 2v6h6 M9 13h6 M9 17h6 M9 9h1",
  "bar-chart": "M3 3v18h18 M18 17V9 M13 17V5 M8 17v-4",
  briefcase: "M20 7h-4V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a1 1 0 0 0-1 1v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a1 1 0 0 0-1-1Z M8 7V5h8v2 M3 12h18",
  // An archive box: lid, body, and the label slot on its front face. Nothing
  // in the existing set reads as "stored inventory" - briefcase is already
  // Recruitment's, and layers/list are too abstract for a physical register.
  archive: "M3 4h18v4H3V4Z M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8 M10 12h4",
  sliders: "M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M1 14h6 M9 8h6 M17 16h6",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z",
  "shield-check": "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z M9 12l2 2 4-4",
  list: "M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01",
  layers: "m12 2 9 5-9 5-9-5 9-5Z M3 12l9 5 9-5 M3 17l9 5 9-5",
  server:
    "M20 4H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z M20 14H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2Z M6 8h.01 M6 18h.01",
  "external-link": "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6 M15 3h6v6 M10 14 21 3",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9",
  "chevron-down": "m6 9 6 6 6-6",
  "chevron-right": "m9 18 6-6-6-6",
  "chevron-left": "m15 18-6-6 6-6",
  menu: "M3 12h18 M3 6h18 M3 18h18",
  x: "M18 6 6 18 M6 6l12 12",
  bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3",
  refresh: "M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0 1 14.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
  plus: "M12 5v14 M5 12h14",
  search: "m21 21-4.35-4.35 M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z",
  lock: "M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Z M7 11V7a5 5 0 0 1 10 0v4",
  key: "M7 12a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z M9 13 19 3 M16 6 18 8 M13 9 15 11",
  "check-circle": "M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3",
  "x-circle": "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M15 9l-6 6 M9 9l6 6",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z",
  "help-circle": "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3 M12 17h.01",
  activity: "M22 12h-4l-3 9L9 3l-3 9H2",
  info: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M12 16v-4 M12 8h.01",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
  "eye-off":
    "M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24 M1 1l22 22",
  upload: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12",
  trash: "M3 6h18 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M10 11v6 M14 11v6",
  edit: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z",
  send: "m22 2-7 20-4-9-9-4Z M22 2 11 13",
  "clipboard-list": "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2 M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2 M9 12h6 M9 16h6 M9 9h1",
  gift: "M20 12v10H4V12 M2 7h20v5H2z M12 22V7 M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z",
  // Three vertical dots — overflow/"more actions" menu trigger
  "more-vertical": "M12 5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z M12 12a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z M12 19a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z",
  // Pause (two vertical bars) — "disable device"
  pause: "M6 4h4v16H6Z M14 4h4v16h-4Z",
  // Play (right-pointing triangle) — "enable device"
  play: "M5 3l14 9-14 9V3Z",
  "credit-card": "M1 4h22a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z M1 10h22",
  // A right-pointing arrow - the premium LoginPage's "Sign in" button.
  "arrow-right": "M5 12h14 M12 5l7 7-7 7",
  "trending-up": "M23 6 13.5 15.5 8.5 10.5 1 18 M17 6h6v6",
  // Task Board glyphs (2026-10-14): kanban = three lanes in a frame, grip =
  // the 2x3 drag-handle dots, message-square = comment bubble, check-square
  // = checklist, flag = priority, tag = label, check = plain tick.
  kanban: "M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M8 7v6 M12 7v10 M16 7v3",
  grip: "M9 5h.01 M9 12h.01 M9 19h.01 M15 5h.01 M15 12h.01 M15 19h.01",
  "message-square": "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z",
  "check-square": "M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  flag: "M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1Z M4 22v-7",
  tag: "M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z M7 7h.01",
  check: "M20 6 9 17l-5-5",
  // A bullseye (three concentric rings) for Performance Management - "goals
  // and targets" being the module's own vocabulary. Drawn as arc paths
  // (this renderer only supports <path>, not Feather's native <circle>).
  target:
    "M2 12a10 10 0 1 0 20 0a10 10 0 1 0-20 0 M6 12a6 6 0 1 0 12 0a6 6 0 1 0-12 0 M10 12a2 2 0 1 0 4 0a2 2 0 1 0-4 0",
  // A map pin for Branch Management - "a physical location", distinct from
  // building (a single company/HQ concept) and layers (abstract grouping).
  "map-pin": "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z M12 10a3 3 0 1 0 0 6a3 3 0 1 0 0-6",
  // A phone handset (Feather's own "phone" glyph) for Call Analytics -
  // nothing in the existing set reads as "a telephone call".
  phone:
    "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z",
  // 2026-08-18 (Dynamic-Element Accent pass): Shift Management previously
  // shared "clock" with Attendance - two different module tiles showing an
  // identical glyph side by side in the sidebar/module grid. A sunrise
  // (horizon + rising sun + rays) reads as "shift/roster" (morning/evening/
  // night rotation) without colliding with clock's "time of day" meaning.
  sunrise:
    "M17 18a5 5 0 0 0-10 0 M12 2v7 M4.22 10.22l1.42 1.42 M16.36 10.22l1.42 1.42 M1 18h2 M21 18h2 M23 22H1 M8 6l4-4 4 4",
  // Expense Management previously shared "wallet" with Payroll - a receipt
  // (torn-edge slip + line items) reads as "itemized spend/claim," distinct
  // from wallet's "where salary lives."
  receipt:
    "M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8 M12 17.5v-11",
};

export function Icon({ name, className, ...props }: { name: string } & SVGProps<SVGSVGElement>) {
  const d = PATHS[name];
  if (!d) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {d.split(" M").map((segment, i) => (
        <path key={i} d={i === 0 ? segment : "M" + segment} />
      ))}
    </svg>
  );
}
